import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, ArrowRight, Search, BookOpen } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setBlogPosts(data || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(blogPosts.map(post => post.category))];
  
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen py-16 relative">
      {/* Background decorative elements */}
      <div className="absolute top-40 left-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 right-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      
      <div className="section-container relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-8">
            <BookOpen size={16} className="text-cyan-400" />
            <span>Stories, Tutorials & Updates</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold font-poppins mb-6">
            Our <span className="gradient-text">Blog</span>
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            Insights, tutorials, and updates from the Script Saint team
          </p>
        </div>

        {/* Search and Filter */}
        <div className="glass-card p-6 md:p-8 mb-12 rounded-2xl border border-cyan-500/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-cyan-400 transition" size={20} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition appearance-none cursor-pointer"
            >
              <option value="all">All Categories</option>
              {categories.filter(c => c !== 'all').map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Blog Posts */}
        {loading ? (
          <div className="text-center text-gray-400 py-32">
            <div className="relative w-20 h-20 mx-auto mb-8">
              <div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-xl font-semibold">Loading blog posts...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we fetch the latest articles</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center text-gray-400 py-32">
            <div className="glass-card max-w-md mx-auto p-12 rounded-3xl">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-400/20 to-teal-500/20 rounded-full flex items-center justify-center">
                <BookOpen size={40} className="text-cyan-400" />
              </div>
              <p className="text-3xl font-bold mb-4">No blog posts found</p>
              <p className="text-base text-gray-500">Check back soon for our latest articles</p>
            </div>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Featured Post */}
            {filteredPosts[0] && (
              <div className="glass-card overflow-hidden rounded-3xl card-hover group border border-cyan-500/10" data-testid="featured-blog-post">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  <div className="relative h-96 lg:h-auto overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
                    <img 
                      src={filteredPosts[0].image_url || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&h=600&fit=crop'} 
                      alt={filteredPosts[0].title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-6 left-6">
                      <span className="px-5 py-2 bg-gradient-to-r from-cyan-400 to-teal-500 text-gray-900 text-sm font-bold rounded-full uppercase shadow-lg">
                        Featured
                      </span>
                    </div>
                  </div>
                  <div className="p-10 md:p-12 flex flex-col justify-center">
                    <div className="flex items-center flex-wrap gap-4 text-gray-400 text-sm mb-6">
                      <span className="px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-full font-semibold">{filteredPosts[0].category}</span>
                      <div className="flex items-center space-x-2">
                        <Calendar size={16} />
                        <span>{new Date(filteredPosts[0].created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock size={16} />
                        <span>{filteredPosts[0].read_time || '5'} min read</span>
                      </div>
                    </div>
                    <h2 className="text-4xl font-bold text-white mb-6 group-hover:text-cyan-400 transition-colors leading-tight">
                      {filteredPosts[0].title}
                    </h2>
                    <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                      {filteredPosts[0].excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
                          <User size={24} className="text-gray-900" />
                        </div>
                        <div>
                          <div className="text-white font-semibold">{filteredPosts[0].author || 'Script Saint Team'}</div>
                        </div>
                      </div>
                      <Link 
                        to={`/blog/${filteredPosts[0].slug || filteredPosts[0].id}`}
                        className="text-cyan-400 hover:text-cyan-300 transition flex items-center space-x-2 font-semibold"
                      >
                        <span>Read More</span>
                        <ArrowRight size={20} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other Posts */}
            {filteredPosts.length > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.slice(1).map((post) => (
                  <div 
                    key={post.id} 
                    className="glass-card overflow-hidden rounded-2xl card-hover group border border-cyan-500/10"
                    data-testid={`blog-post-${post.id}`}
                  >
                    <div className="relative h-56 overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
                      <img 
                        src={post.image_url || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=600&h=400&fit=crop'} 
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute top-4 left-4">
                        <span className="px-4 py-1.5 bg-gradient-to-r from-cyan-400 to-teal-500 text-gray-900 text-xs font-bold rounded-full uppercase shadow-lg">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center space-x-4 text-gray-500 text-xs mb-3">
                        <div className="flex items-center space-x-1">
                          <Calendar size={14} />
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock size={14} />
                          <span>{post.read_time || '5'} min</span>
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-400 mb-4 line-clamp-3 leading-relaxed">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-800/50">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-lg flex items-center justify-center">
                            <User size={16} className="text-gray-900" />
                          </div>
                          <span className="text-sm text-gray-400">{post.author || 'Admin'}</span>
                        </div>
                        <Link 
                          to={`/blog/${post.slug || post.id}`}
                          className="text-cyan-400 hover:text-cyan-300 transition text-sm flex items-center space-x-1 font-semibold"
                        >
                          <span>Read</span>
                          <ArrowRight size={16} />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;